import {
  CheckSquare2Icon,
  DownloadIcon,
  FileCheckIcon,
  FileIcon,
  MoreHorizontalIcon,
  PencilIcon,
  TrashIcon,
  UploadIcon,
} from "lucide-react";
import {
  createContext,
  forwardRef,
  type Key,
  useContext,
  useEffect,
  useState,
} from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { RoleCondition } from "@/components/RoleCondition";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateHeading,
  EmptyStateIcon,
} from "@/components/ui/empty-state";
import { Input, Label, TextField } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { ListItemLeft, ListItemRight } from "@/components/ui/list-item";
import {
  Menu,
  MenuItem,
  MenuSeparator,
  MenuSubTrigger,
  MenuTrigger,
} from "@/components/ui/menu";
import { Modal } from "@/components/ui/modal";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { getApiUrl } from "@/lib/api";
import type { TAccessionDetail, TAccessionUpload } from "@/lib/types";
import { format } from "@/lib/utils";
import { formatFileSize } from "@/lib/utils/units";
import { mutationResponse } from "@/utils/trpc";
import { useSetDialogState } from "./state";

type UploadContextValue = {
  deleteUpload: { id: number } | null;
  setDeleteUpload: (upload: { id: number } | null) => void;
  renameUpload: { id: number; name: string } | null;
  setRenameUpload: (upload: { id: number; name: string } | null) => void;
};
const UploadContext = createContext({} as UploadContextValue);

export function AccessionUploads({
  accession,
}: {
  accession: TAccessionDetail;
}) {
  const uploads = trpc.accession.uploads.list.useQuery(accession.id);
  const setUploadOpen = useSetDialogState("upload");
  const [deleteUpload, setDeleteUpload] = useState<{ id: number } | null>(null);
  const [renameUpload, setRenameUpload] = useState<{
    id: number;
    name: string;
  } | null>(null);

  return (
    <UploadContext.Provider
      value={{ deleteUpload, setDeleteUpload, renameUpload, setRenameUpload }}
    >
      <NavList
        items={uploads.data || []}
        isLoading={uploads.isPending}
        loadingItems={2}
        emptyState={
          <EmptyState>
            <EmptyStateIcon>
              <UploadIcon />
            </EmptyStateIcon>
            <EmptyStateHeading>No uploads</EmptyStateHeading>
            <EmptyStateDescription>
              No documents or results have been uploaded on this accession.
            </EmptyStateDescription>
            <RoleCondition
              roles={[
                "blis_lab_assistant",
                "blis_lab_tech",
                "blis_lab_manager",
              ]}
            >
              <EmptyStateActions>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => setUploadOpen({ files: [] })}
                >
                  Upload file
                </Button>
              </EmptyStateActions>
            </RoleCondition>
          </EmptyState>
        }
        className="border-b divide-y"
      >
        {(upload) => (
          <NavListItem
            asChild
            className="focus-visible:ring-0 hover:bg-transparent"
            key={upload.id}
          >
            <UploadItem upload={upload} key={upload.id} />
          </NavListItem>
        )}
      </NavList>
      <DeleteUploadDialog />
      <RenameUploadDialog />
    </UploadContext.Provider>
  );
}

const UploadItem = forwardRef<HTMLAnchorElement, { upload: TAccessionUpload }>(
  ({ upload }, ref) => {
    const { setDeleteUpload, setRenameUpload } = useContext(UploadContext);
    const url = getApiUrl(`/api/data/uploads${upload.filePath}`);
    const setDialog = useSetDialogState("results");

    const mutation = trpc.accession.uploads.update.useMutation(
      mutationResponse({
        errorTitle: "Error updating upload type",
        successMessage: "Upload type updated",
      }),
    );

    const utils = trpc.useUtils();

    function setUploadType(type: TAccessionUpload["type"]) {
      mutation.mutate({ id: upload.id, type });
    }

    async function enterResults() {
      const toastId = toast.loading("Reading results...");
      try {
        const res = await utils.results.parseUploadResults.fetch({
          id: upload.id,
        });
        setDialog(res);
      } catch (_err) {
        toast.error("Error reading results", {
          description: "Please try again or re-upload the results.",
        });
      } finally {
        toast.dismiss(toastId);
      }
    }

    function download() {
      if (upload.filePath) {
        const link = document.createElement("a");
        link.href = url;
        link.download = upload.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }

    const onAction = (action: Key) => {
      switch (action) {
        case "download":
          download();
          break;
        case "enterResults":
          enterResults();
          break;
        case "delete":
          setDeleteUpload(upload);
          break;
        case "setDocument":
          setUploadType("DOCUMENT");
          break;
        case "setReport":
          setUploadType("REPORT");
          break;
        case "setResults":
          setUploadType("RESULTS");
          break;
        case "rename":
          setRenameUpload(upload);
          break;
      }
    };

    return (
      <a
        ref={ref}
        className="flex items-center outline-hidden py-2 hover:bg-muted/50"
        href={url}
        target="_blank"
        rel="noreferrer"
      >
        <ListItemLeft>
          <div className="flex items-center gap-2 min-w-0">
            {upload.type === "DOCUMENT" ? (
              <FileIcon data-slot="icon" className="size-4 shrink-0" />
            ) : upload.type === "REPORT" ? (
              <FileCheckIcon data-slot="icon" className="size-4 shrink-0" />
            ) : (
              <CheckSquare2Icon data-slot="icon" className="size-4 shrink-0" />
            )}
            <div className="flex flex-col min-w-0">
              <Text size="1" truncate>
                {upload.name}
              </Text>
              <Text size="1" muted>
                {formatFileSize(upload.size)}
              </Text>
            </div>
          </div>
        </ListItemLeft>
        <ListItemRight>
          <Text muted size="1">
            {format(upload.createdAt)}
          </Text>
          <UserAvatar tooltip user={upload.createdBy} />
          <RoleCondition roles={["blis_lab_tech", "blis_lab_manager"]}>
            <MenuTrigger>
              <Button variant="ghost">
                <MoreHorizontalIcon data-slot="icon" />
              </Button>
              <Menu aria-label="Upload actions" onAction={onAction}>
                <MenuItem id="download">
                  <DownloadIcon data-slot="icon" />
                  Download
                </MenuItem>
                <MenuItem id="rename">
                  <PencilIcon data-slot="icon" />
                  Rename
                </MenuItem>
                <MenuSubTrigger>
                  <MenuItem>
                    <FileIcon data-slot="icon" />
                    Set type
                  </MenuItem>
                  <Menu aria-label="Set upload type" onAction={onAction}>
                    <MenuItem
                      id="setDocument"
                      isDisabled={upload.type === "DOCUMENT"}
                    >
                      <FileIcon data-slot="icon" />
                      Document
                    </MenuItem>
                    <MenuItem
                      id="setReport"
                      isDisabled={upload.type === "REPORT"}
                    >
                      <FileCheckIcon data-slot="icon" />
                      Report
                    </MenuItem>
                    <MenuItem
                      id="setResults"
                      isDisabled={upload.type === "RESULTS"}
                    >
                      <CheckSquare2Icon data-slot="icon" />
                      Results
                    </MenuItem>
                  </Menu>
                </MenuSubTrigger>
                {upload.type === "RESULTS" && (
                  <MenuItem id="enterResults">
                    <CheckSquare2Icon data-slot="icon" />
                    Read results
                  </MenuItem>
                )}
                <MenuSeparator />
                <MenuItem id="delete" isDanger>
                  <TrashIcon data-slot="icon" />
                  Delete
                </MenuItem>
              </Menu>
            </MenuTrigger>
          </RoleCondition>
        </ListItemRight>
      </a>
    );
  },
);

function DeleteUploadDialog() {
  const { deleteUpload, setDeleteUpload } = useContext(UploadContext);
  const mutation = trpc.accession.uploads.delete.useMutation(
    mutationResponse({
      errorTitle: "Error deleting   file",
      successMessage: "File deleted",
      onSuccess() {
        setDeleteUpload(null);
      },
    }),
  );
  return (
    <ConfirmPrompt
      open={!!deleteUpload}
      onOpenChange={(flag) => {
        if (!flag) {
          setDeleteUpload(null);
        }
      }}
      title="Delete file"
      message="Are you sure you want to delete this file?"
      onConfirm={() => {
        if (deleteUpload) {
          mutation.mutate(deleteUpload.id);
        }
      }}
      loading={mutation.isPending}
      danger
    />
  );
}

function RenameUploadDialog() {
  const { renameUpload, setRenameUpload } = useContext(UploadContext);
  const [name, setName] = useState("");
  const mutation = trpc.accession.uploads.update.useMutation(
    mutationResponse({
      errorTitle: "Error renaming file",
      successMessage: "File renamed",
      onSuccess() {
        setRenameUpload(null);
      },
    }),
  );
  useEffect(() => {
    if (renameUpload?.name) {
      setName(renameUpload.name);
    }
  }, [renameUpload?.name]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (renameUpload) {
      mutation.mutate({ id: renameUpload.id, name });
    }
  };

  return (
    <Modal
      isOpen={!!renameUpload}
      onOpenChange={(flag) => {
        if (!flag) {
          setRenameUpload(null);
        }
      }}
    >
      <Dialog>
        <DialogHeader>
          <DialogTitle>Rename file</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Form id="rename-form" onSubmit={handleSubmit}>
            <TextField
              name="name"
              value={name}
              onChange={(value) => setName(value)}
              isRequired
              autoFocus
            >
              <Label>Name</Label>
              <Input />
            </TextField>
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button
            isPending={mutation.isPending}
            isDisabled={mutation.isPending}
            form="rename-form"
            type="submit"
          >
            Rename
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
